import { useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { isApiError, type Versioned } from '../../api/client';
import { useApi } from '../../api/context';
import { queryKeys } from '../../api/queries';
import type { AddressPlan, Network } from '../../api/types';
import { useToast } from '../../components/toast';
import { Button, Dialog } from '../../components/ui';
import { useOptionalDock } from '../../dock/context';
import { usePhone } from '../../shell/breakpoints';
import { strings } from '../../strings';
import { AddressPlanFields } from './AddressPlanFields';
import { cidrsOverlap } from './cidr';
import { NetworkDiagnosticList } from './parts';
import { checkPlanForm, errorsAtFn, formToPlan, planToForm, type AddressPlanForm } from './planForm';
import { useNetworkValidation } from './validation';

const a = strings.networks.lists.addressPlan;
const p = strings.networks.lists.plan;

/**
 * "Edit address plan" (spec 11 6.15.8): the plan's fields, a dry run 400 ms after each change with its diagnostics at
 * the fields, Remove disabled for supernets with allocations, Save (PATCH with If-Match) only without errors.
 */
export function EditAddressPlanDialog({
  network,
  plan,
  onClose,
}: {
  network: Versioned<Network>;
  plan: AddressPlan | undefined;
  onClose: () => void;
}) {
  const api = useApi();
  const toast = useToast();
  const dock = useOptionalDock();
  const phone = usePhone();
  const queryClient = useQueryClient();
  const ids = useId();
  const [target, setTarget] = useState(network);
  const [form, setForm] = useState<AddressPlanForm>(() => planToForm(network.data.settings.addressPlan));
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const browser = checkPlanForm(form);
  const browserOk = Object.keys(browser).length === 0;
  const patch = browserOk ? { settings: { addressPlan: formToPlan(form) } } : null;
  const dry = useNetworkValidation(network.data.id, patch);
  const diagnostics = dry.data?.diagnostics ?? [];
  const errorsAt = errorsAtFn(browser, diagnostics);
  const fieldPaths = (d: { path: string }) => d.path.startsWith('/settings/addressPlan/');
  const other = diagnostics.filter((d) => !fieldPaths(d));
  const hasErrors = !browserOk || diagnostics.some((d) => d.severity === 'error');
  const canSave = !busy && !dry.pending && !!dry.data && !hasErrors;
  const blocks = (plan?.regions ?? []).flatMap((r) => r.blocks);
  const removeReason = (cidr: string) =>
    network.data.settings.addressPlan.supernets.includes(cidr) && blocks.some((b) => cidrsOverlap(b, cidr))
      ? p.supernetInUse(cidr)
      : null;

  const save = async () => {
    if (!patch || !canSave) return;
    setBusy(true);
    setMessage(null);
    try {
      const saved = await api.patchNetwork(target.data.id, patch, target.etag);
      queryClient.setQueryData(queryKeys.network(saved.data.id), saved);
      void queryClient.invalidateQueries({ queryKey: ['network', saved.data.id] });
      void queryClient.invalidateQueries({ queryKey: ['networks'] });
      dock?.announce(a.saved);
      toast.show({ tone: 'success', title: a.saved });
      onClose();
    } catch (error) {
      if (isApiError(error, 'PRECONDITION_FAILED')) {
        const fresh = await api.getNetwork(target.data.id);
        queryClient.setQueryData(queryKeys.network(fresh.data.id), fresh);
        setTarget(fresh);
        setMessage(a.changedMeanwhile(fresh.data.name));
      } else if (isApiError(error) && error.diagnostics.length) {
        setMessage(error.detail ?? error.message);
      } else toast.error(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={a.editTitle}
      onClose={onClose}
      wide
      className={phone ? 'dialog-sheet' : undefined}
      footer={
        <>
          {dry.pending && (
            <span role="status" className="mr-auto self-center text-sm text-fg-muted">
              {a.checking}
            </span>
          )}
          {!!dry.error && !dry.pending && (
            <span role="status" className="mr-auto self-center text-sm text-fg-muted">
              {a.checkFailed}
            </span>
          )}
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button variant="primary" disabled={!canSave} onClick={() => void save()}>
            {busy ? a.saving : a.save}
          </Button>
        </>
      }
    >
      {message && (
        <p role="status" className="callout callout-warning mb-3">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          {message}
        </p>
      )}
      <AddressPlanFields
        idPrefix={ids}
        value={form}
        onChange={setForm}
        errorsAt={errorsAt}
        removeReason={removeReason}
      />
      {other.length > 0 && (
        <div className="mt-2">
          <NetworkDiagnosticList diagnostics={other} />
        </div>
      )}
    </Dialog>
  );
}
